import React, { FunctionComponent, useCallback, useEffect, useReducer, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Button, Card, CircularLoader, Dropdown, Pill, isMobileDevice } from 'rs-emd-ui-atoms';
import { getLabel } from '../../../../helpers/html.utils';
import { Validation } from '../../../../helpers/validation.utils';
import { IPrice } from '../../../../models/common/pricing/price.model';
import { StockStatus } from '../../../../models/common/stock.model';
import { DTPStockBadge, EnergyLabelProductInfo, ProductInfoPill } from '../../../shared';
import { Action, ListProductCardActionType, ListProductCardProps, State } from './list-product-card-component.model';
import styles from './list-product-card-component.module.scss';

export const ListProductCard: FunctionComponent<ListProductCardProps> = (props: ListProductCardProps) => {
  const [state, dispatch] = useReducer(reducer, {
    quantity: props.product.priceArea?.minimumOrderQuantity ?? 1,
    totalPrice: props.product.priceArea?.totalPrice,
    unitPrice: props.product.priceArea?.price?.priceAfter?.displayValue,
    isValidQty: true,
    isBusy: false,
    isStockLoading: false,
    prices: [],
    isOverflowMenuOpen: false,
    isDropdownOpen: false,
    defaultDropdownSelection: props.product.priceArea?.minimumOrderQuantity ?? 1,
    isInBasket: props.product.priceArea?.isInBasket ?? false,
  });

  let searchValueTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const hasDiscount = props.product.priceArea?.price?.priceAfter?.value !== props.product.priceArea?.price?.priceBefore?.value;

  let hideAddToBasket =
    props.product.stockArea?.isUsingDTP &&
    props.product.stockArea?.statusCode &&
    [
      StockStatus.CurrentlyUnavailable,
      StockStatus.CurrentlyUnavailableBeingDiscontinued,
      StockStatus.CurrentlyUnavailableBeingRemoved,
      StockStatus.Discontinued,
      StockStatus.Removed,
    ].includes(props.product.stockArea?.statusCode);

  /* Add to parts list*/
  const partsListBtn = (
    <Button
      icon='star-outline'
      buttonType='tertiary'
      size='small-icon'
      onClick={onAddToPartsListClick}
      title={getLabel(props.labels, 'add_to_parts_list')}
    />
  );

  // set stock
  useEffect(() => {
    dispatch({ type: ListProductCardActionType.SetStockData, stock: props.product.stockArea });
  }, [props.product.stockArea]);

  //get price for quantity not listed in dropdown values
  useEffect(() => {
    if (state.isSearchingUnlistedQuantity && state.isValidQty) {
      const qtySearched = state.quantity;

      props.pricingService.getPricesByQty({ stockCode: props.product.title.stockCode, quantity: qtySearched }).then((res) => {
        const pricesIncVat =
          props.incVat && res.priceArea?.prices !== undefined && res.priceArea?.prices.length > 0 ? res.priceArea?.prices : undefined;

        const pricesExVat =
          !props.incVat && res.priceArea?.pricesExVat !== undefined && res.priceArea?.pricesExVat.length > 0
            ? res.priceArea?.pricesExVat
            : undefined;

        const prices = pricesExVat ?? pricesIncVat ?? undefined;

        dispatch({
          type: ListProductCardActionType.SetUnlistedQuantityPrice,
          qty: qtySearched,
          price: prices ? (prices[0].totalPrice as IPrice) : undefined,
          unitPrice: prices ? prices[0].unitPrice?.priceAfter?.displayValue : undefined,
          message: res.priceArea && res.priceArea.errorMessage.length > 0 ? res.priceArea?.errorMessage : undefined,
        });
      });
    } else if (state.unlistedQuantityPrice) {
      //if changing search from an unlisted quantity to another quantity
      dispatch({ type: ListProductCardActionType.SetUnlistedQuantityPrice, price: undefined });
    }
  }, [
    props.incVat,
    props.pricingService,
    props.product.title.stockCode,
    state.isSearchingUnlistedQuantity,
    state.isValidQty,
    state.quantity,
    state.unlistedQuantityPrice,
  ]);

  //useCallback required to run only when state changes & when fn is called
  const getDropDownValues = useCallback(() => {
    // if errormessage and qty is not a substring of listed value
    if (
      !state.isValidQty &&
      state.prices.map((p) => p.quantity.toString()).filter((p) => p.includes(state.quantity.toString().toLowerCase())).length === 0
    )
      return [];
    if (state.prices.length > 0) {
      if (!state.isSearchingUnlistedQuantity) {
        return state.prices.map((p) => {
          return {
            key: p.quantity,
            elt: (
              <div className={styles['qty-opt']}>
                <span>{p.quantity}</span>
                <span className={styles['qty-price']}>{p.totalPrice.priceAfter?.displayValue}</span>
              </div>
            ),
            searchableString: p.quantity.toString(),
          };
        });
      } else {
        return [
          {
            key: state.quantity,
            elt: (
              <div className={styles['qty-opt']}>
                <span>{state.quantity}</span>
                {state.unlistedQuantityPrice ? (
                  <span className={styles['qty-price']}>{state.unlistedQuantityPrice.priceAfter?.displayValue}</span>
                ) : (
                  <CircularLoader />
                )}
              </div>
            ),
            searchableString: state.quantity.toString(),
          },
        ];
      }
    } else {
      return [
        {
          key: props.product.priceArea?.minimumOrderQuantity ?? 1,
          elt: (
            <div className={styles['qty-opt']}>
              <span>{props.product.priceArea?.minimumOrderQuantity}</span>
              <span className={styles['qty-price']}>{props.product.priceArea?.price?.priceAfter?.displayValue}</span>
            </div>
          ),
          searchableString: props.product.priceArea?.minimumOrderQuantity.toString(),
        },
      ];
    }
  }, [
    props.product.priceArea,
    state.isSearchingUnlistedQuantity,
    state.isValidQty,
    state.prices,
    state.quantity,
    state.unlistedQuantityPrice,
  ]);

  let discount = hasDiscount && <p className={styles.discount}>{state.totalPrice?.priceBefore?.displayValue}</p>;

  /* Product Restricted or barred */
  let restrictedPill = (
    <ProductInfoPill
      type={props.product.punchoutInfoArea?.isBarred ? 'barred' : 'restricted'}
      title={
        props.product.punchoutInfoArea?.isBarred
          ? getLabel(props.labels, 'punchout_barred_product')
          : getLabel(props.labels, 'punchout_restricted_product')
      }
      message={
        props.product.punchoutInfoArea?.isBarred
          ? getLabel(props.labels, 'punchout_barred_info_short')
          : getLabel(props.labels, 'punchout_restricted_info_short')
      }
      modalCTA={getLabel(props.labels, 'view_details')}
      modalTitle={
        props.product.punchoutInfoArea?.isBarred
          ? getLabel(props.labels, 'punchout_barred_product')
          : getLabel(props.labels, 'punchout_restricted_product')
      }
      modalMessage={
        props.product.punchoutInfoArea?.isBarred
          ? getLabel(props.labels, 'punchout_barred_info')
          : getLabel(props.labels, 'punchout_restricted_info')
      }
    />
  );

  const pdpUrl = props.product.title.pdpUrl + '?backToResults=1';

  return (
    <Card
      className={`${styles['lcard-container']} ${props.isLast ? styles.last : ''} ${!props.isSRP ? styles.grid : styles.srp}`}
      style={props.style}>
      <div className={styles['lcard-header']}>
        <div className={styles['product-badges']}>
          {props.product.stockArea?.isUsingDTP && state.stock?.statusCode !== StockStatus.LocallyManaged && (
            <DTPStockBadge labels={props.labels} stockArea={props.product.stockArea} className={styles['inline']} />
          )}

          {[props.product.productTypeArea, ...(props.product.productTypeArea?.secondaryMessages ?? [])]
            .filter((productType) => productType?.isVisible)
            .map((productType) => {
              if (productType)
                return (
                  <Badge
                    key={`product_type_${productType.title}`}
                    colour={productType.mode === 'WARNING' ? 'yellow' : 'green'}
                    icon={productType.mode ? (productType.mode.toLowerCase() as 'warning' | 'sustainable') : undefined}
                    href={productType.mode === 'SUSTAINABLE' ? getLabel(props.labels, 'better_world_url') : undefined}>
                    {productType.title}
                  </Badge>
                );
            })}

          {props.product.stockArea?.isUsingDTP && state.stock?.statusCode !== StockStatus.LocallyManaged && (
            <DTPStockBadge labels={props.labels} stockArea={props.product.stockArea} className={styles['stacked']} />
          )}
        </div>

        {props.product.stockArea?.isUsingDTP && !hideAddToBasket && !isMobileDevice() && partsListBtn}
      </div>

      <div className={styles.lcard}>
        <div className={styles['info-container']}>
          <Link to={pdpUrl} className={`${styles['thumb-container']}`}>
            <img className={styles.thumb} src={props.product.title.primaryImageUrl} alt={props.product.title.title}></img>
          </Link>

          <div className={styles.details}>
            <div className={styles['title-area']}>
              <Link to={pdpUrl} className={`snippet ${styles.title}`}>
                {props.product.title.title}
              </Link>
              {(!props.product.stockArea?.isUsingDTP || (!hideAddToBasket && isMobileDevice())) && partsListBtn}
            </div>
            {/* PPK */}
            {props.product.ppkArea && props.product.ppkArea.isVisible && (
              <Link to={props.product.ppkArea.pdpURL}>{getLabel(props.labels, 'also_available_in_production_pack')}</Link>
            )}

            {/* Attributes */}
            <div className={styles.attributes}>
              {props.product.title.formattedStockCode && (
                <span>
                  {getLabel(props.labels, 'stock_code')}: {props.product.title.formattedStockCode}
                </span>
              )}
              {props.product.title.brand && (
                <span className={styles.brand}>
                  <span>{getLabel(props.labels, 'brand')}: </span>
                  <a
                    href={`/productlist/search?query=${props.product.title.brand.toLowerCase()}`}
                    onClick={() =>
                      props.productService.brandTrackerClickEvent(
                        props.isSRP ? 'BrandSearchSLP' : 'BrandSearchPLP',
                        props.product.title.brand
                      )
                    }>
                    {props.product.title.brand}
                  </a>
                </span>
              )}
              {props.product.title.mpn && (
                <span>
                  {getLabel(props.labels, 'mpn')}: {props.product.title.mpn}
                </span>
              )}
              {props.product.title.impaCode && props.product.title.impaCode.length > 0 && (
                <span>
                  {getLabel(props.labels, 'impa')}: {props.product.title.impaCode}
                </span>
              )}
              {props.product.title.hsnCode && (
                <span>
                  {getLabel(props.labels, 'hsn')}: {props.product.title.hsnCode}
                </span>
              )}
            </div>

            {/** Energy Label */}
            {props.product.energyArea?.energyLabel && (
              <EnergyLabelProductInfo
                labels={props.labels ?? undefined}
                energyArea={props.product.energyArea}
                stockCode={props.product.title.stockCode}
                productService={props.productService}
              />
            )}
          </div>
        </div>
        <div className={styles.pricing}>
          <div className={`${styles.block} ${styles['price-block']}`}>
            {hasDiscount && <p className={styles['your-price']}>{getLabel(props.labels, 'your_price')}</p>}
            {getPriceLine()}
            {!props.product.stockArea?.isUsingDTP && (
              <div className={styles['stock-check']}>
                {!state.stock && !state.isStockLoading && (
                  <p className='link' onClick={getStockData}>
                    {getLabel(props.labels, 'check_stock')}
                  </p>
                )}
                {state.isStockLoading && (
                  <div className={styles.loading}>
                    <CircularLoader width={16}></CircularLoader>
                    <p className={`link ${styles['stock-check']}`}>{getLabel(props.labels, 'loading_stock')}</p>
                  </div>
                )}
                {state.stock && state.stock.statusCode !== StockStatus.LocallyManaged && (
                  <Pill class={`stock ${state.stock?.statusCode.toLowerCase() ?? ''}`}>{state.stock?.title}</Pill>
                )}
              </div>
            )}
          </div>
          {!hideAddToBasket && (
            <div className={`${styles.block} ${styles['cta-area']}`}>
              {!props.product.punchoutInfoArea?.isBarred && (
                <>
                  <Dropdown
                    onSearchValueChange={onSearchValueChange}
                    onFocus={() => {
                      //to reset list especially for mobile modal
                      if (state.isSearchingUnlistedQuantity) {
                        dispatch({
                          type: ListProductCardActionType.SearchListedQuantity,
                        });
                      }
                    }}
                    isOpen={state.isDropdownOpen}
                    defaultSelection={state.defaultDropdownSelection}
                    className={styles.qty}
                    isSearchable={true}
                    customSearchHeader={mobileSearchHeader()}
                    customSearchHeaderClassName='search-header'
                    searchableInputType='number'
                    additionalInfo={`${getLabel(props.labels, 'available_in_multiples')} ${props.product.priceArea?.ssm}`}
                    list={getDropDownValues()}
                    errorMessage={state.errorMessage}
                    isValid={state.isValidQty}
                    validationCallback={() => state.isValidQty}
                    searchableStepValue={props.product.priceArea?.ssm}
                    minNumericValue={props.product.priceArea?.minimumOrderQuantity ?? 1}
                    selectOnClickOutside={true}
                    onChangeState={(isOpen: boolean) => onPricesDropdownToggle(isOpen)}
                    selectCallback={changeQuantity as (key: string | number) => void}
                  />
                  <Button
                    text={!state.isBusy ? getLabel(props.labels, 'add_to_basket') : getLabel(props.labels, 'adding_to_basket')}
                    className={styles.add}
                    disabled={!state.isValidQty || state.isBusy}
                    onClick={onAddToBasketClick}
                  />
                  <Button
                    icon={!state.isBusy ? 'basket-add' : 'circular-loader'}
                    className={styles['add-icon']}
                    title={getLabel(props.labels, 'add_to_basket')}
                    disabled={!state.isValidQty || state.isBusy}
                    onClick={onAddToBasketClick}
                  />
                </>
              )}
            </div>
          )}
          {(props.product.punchoutInfoArea?.isBarred || props.product.punchoutInfoArea?.isRestricted) && (
            <div className={styles['restricted-pill-pricing']}>{restrictedPill}</div>
          )}
          {state.isInBasket && <p className={styles['in-basket']}>{getLabel(props.labels, 'in_the_basket')}</p>}
        </div>
      </div>
      {(props.product.punchoutInfoArea?.isBarred || props.product.punchoutInfoArea?.isRestricted) && (
        <div className={styles['restricted-pill-xl-desktop']}>{restrictedPill}</div>
      )}
    </Card>
  );

  function reducer(state: State, action: Action): State {
    switch (action.type) {
      case ListProductCardActionType.IsBusy: {
        return {
          ...state,
          isBusy: action.isBusy,
          isDropdownOpen: action.isDropdownOpen ?? state.isDropdownOpen,
          isInBasket: action.isInBasket ?? state.isInBasket,
        };
      }
      case ListProductCardActionType.IsLoadingStock: {
        return {
          ...state,
          isStockLoading: true,
        };
      }
      case ListProductCardActionType.SetStockData: {
        return {
          ...state,
          isStockLoading: false,
          stock: action.stock,
        };
      }
      case ListProductCardActionType.AddPriceData: {
        return {
          ...state,
          prices: action.data.productPrices ?? [],
        };
      }
      case ListProductCardActionType.ToggleDropDownOpen: {
        const setNewPrice = !action.isOpen && state.unlistedQuantityPrice && state.isValidQty;

        return {
          ...state,
          isDropdownOpen: action.isOpen,
          isSearchingUnlistedQuantity: false,
          defaultDropdownSelection: undefined,

          //update prices if dropdown closed
          totalPrice: setNewPrice ? state.unlistedQuantityPrice : state.totalPrice,
          unitPrice: setNewPrice ? state.unlistedQuantityUnitPrice : state.unitPrice,
        };
      }
      case ListProductCardActionType.SetUnlistedQuantityPrice: {
        let isValid = !(action.message && action.message !== '');
        const doesQtyMatchSearched = (action.qty ?? state.quantity) === state.quantity;

        const setNewPrice = !state.isDropdownOpen && action.price && action.qty === state.quantity && isValid;

        return {
          ...state,
          unlistedQuantityPrice: doesQtyMatchSearched ? action.price : undefined,
          unlistedQuantityUnitPrice: doesQtyMatchSearched ? action.unitPrice : undefined,
          isValidQty: doesQtyMatchSearched ? isValid : state.isValidQty,
          errorMessage: doesQtyMatchSearched ? action.message : state.errorMessage,
          defaultDropdownSelection: doesQtyMatchSearched
            ? isValid
              ? state.defaultDropdownSelection
              : undefined //else it defaults to set value
            : state.defaultDropdownSelection,

          //update prices if dropdown closed
          totalPrice: setNewPrice ? action.price : state.totalPrice,
          unitPrice: setNewPrice ? action.unitPrice : state.unitPrice,
        };
      }
      case ListProductCardActionType.SetInvalidUnlistedQty: {
        return {
          ...state,
          quantity: action.quantity,
          defaultDropdownSelection: undefined,
          unlistedQuantityPrice: undefined,
          isValidQty: false,
          errorMessage: props.product.priceArea?.qtyErrors?.find((e) => e.errorCode === action.errorCode)?.errorMessage,
        };
      }
      case ListProductCardActionType.SearchUnlistedQuantity: {
        return {
          ...state,
          quantity: action.quantity,
          isValidQty: true, // temporarily set to true, pending query result
          isSearchingUnlistedQuantity: true,
          defaultDropdownSelection: undefined,
        };
      }
      case ListProductCardActionType.SearchListedQuantity: {
        return {
          ...state,
          quantity: action.quantity ?? state.quantity,
          isSearchingUnlistedQuantity: false,
          defaultDropdownSelection: undefined, //else it defaults to set value
          isValidQty: action.quantity ? true : state.isValidQty,
          errorMessage: action.quantity ? '' : state.errorMessage,
          unlistedQuantityPrice: undefined,
          unlistedQuantityUnitPrice: undefined,
        };
      }
      case ListProductCardActionType.SetPrice: {
        return {
          ...state,
          quantity: action.qty,
          totalPrice: action.totalPrice ?? state.totalPrice,
          unitPrice: action.unitPrice ?? state.unitPrice,
        };
      }
    }
  }

  function changeQuantity(quantity: number) {
    if (!state.isSearchingUnlistedQuantity) {
      const price = state.prices.filter((p) => p.quantity === quantity)[0];
      const totalPrice = price.totalPrice;
      const unitPrice = price.unitPrice.priceAfter?.displayValue;

      dispatch({ type: ListProductCardActionType.SetPrice, qty: quantity, totalPrice: totalPrice, unitPrice: unitPrice });
    }
  }

  function onAddToBasketClick() {
    if (state.isValidQty) {
      dispatch({ type: ListProductCardActionType.IsBusy, isBusy: true });
      props
        .addToBasket(props.product.title.stockCode, state.quantity)
        .then(() => dispatch({ type: ListProductCardActionType.IsBusy, isBusy: false, isDropdownOpen: false, isInBasket: true }));
    }
  }

  function onAddToPartsListClick() {
    props.onAddToPartsList(
      props.product.title.stockCode,
      state.isValidQty ? state.quantity : props.product.priceArea?.minimumOrderQuantity ?? 1
    );
  }

  function onPricesDropdownToggle(isOpen: boolean) {
    dispatch({ type: ListProductCardActionType.ToggleDropDownOpen, isOpen: isOpen });

    if (isOpen && !state.prices.length) {
      props.pricingService
        .getPrices({ stockCode: props.product.title.stockCode })
        .then((res) => dispatch({ type: ListProductCardActionType.AddPriceData, data: res }));
    }
  }

  function getPriceLine() {
    return (
      <div className={styles['price-line']}>
        <div className={styles.price}>
          <p className={styles['unit-price']}>{state.totalPrice?.priceAfter?.displayValue}</p>
          {discount}
        </div>
        {/* hide extra info if POA */}

        {props.product.priceArea?.price?.priceAfter?.value !== 0 && (
          <p className={styles.container}>
            {discount}
            {state.unitPrice} {props.product.priceArea?.container}&nbsp;
            {getLabel(props.labels, props.incVat ? 'inc_vat_brackets' : 'ex_vat_brackets')}
          </p>
        )}
      </div>
    );
  }

  function mobileSearchHeader() {
    return (
      <div className={styles['modal-search-header']}>
        <span className={styles.title}>{getLabel(props.labels, 'select_quantity')}</span>
        <div className={styles.info}>
          <div className={styles['product-info']}>
            <Link to={pdpUrl} className={`${styles['modal-thumb-container']} ${styles.xsm}`}>
              <img className={styles.thumb} src={props.product.title.primaryImageUrl} alt={props.product.title.title}></img>
            </Link>
            <div className={styles.details}>
              <span>{props.product.title.title}</span>
              {getPriceLine()}
            </div>
          </div>
          <div className={styles['cta-area']}>
            <Button
              text={getLabel(props.labels, 'add_to_basket')}
              className={styles.add}
              disabled={!state.isValidQty || state.isBusy}
              onClick={onAddToBasketClick}
            />
          </div>
        </div>
      </div>
    );
  }

  function onSearchValueChange(value: string) {
    let numVal = Number(value);

    if (searchValueTimeoutRef.current) clearTimeout(searchValueTimeoutRef.current);

    let validationRes = Validation.validateQuantity(
      props.product.priceArea?.minimumOrderQuantity ?? 1,
      props.product.priceArea?.ssm ?? 1,
      value
    );

    //if unlisted quantity
    if (state.prices.map((p) => p.quantity.toString()).filter((p) => p === value.toLowerCase()).length === 0) {
      if (validationRes.isValid) {
        //throttle 300ms
        searchValueTimeoutRef.current = setTimeout(() => {
          dispatch({
            type: ListProductCardActionType.SearchUnlistedQuantity,
            quantity: numVal,
          });
        }, 300);
      } else {
        dispatch({ type: ListProductCardActionType.SetInvalidUnlistedQty, quantity: numVal, errorCode: validationRes.errorCode });
      }
    }
    //if listed quantity
    else {
      dispatch({
        type: ListProductCardActionType.SearchListedQuantity,
        quantity: numVal,
      });
    }
  }

  function getStockData() {
    dispatch({ type: ListProductCardActionType.IsLoadingStock });
    props.productService.getStockData({ stockCode: props.product.title.stockCode }).then((data) => {
      dispatch({ type: ListProductCardActionType.SetStockData, stock: data.stockArea });
    });
  }
};
